/* Purchases/Home/PurchasesPageContainer.js */
import React, { ComponentType } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';
import { Link } from '~/lib/react-router-dom';

import { Box, Container, Tab, Tabs } from '@mui/material';

import CartPaneContainer from '~/components/Cart/CartPaneContainer';
import PageHeader from '~/components/Common/Elements/PageHeader';
import UserBreadcrumbs from '~/components/Common/Elements/UserBreadcrumbs';
import SmsVerifyWarning from '~/components/Common/SmsVerifyWarning';
import { preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';
import AllBusinessesOrderSyncButton from '~/components/LeBusinessTraveller/components/OrderSync/AllBusinessesOrderSyncButton';

import {
  ROLE_ADMIN_USER,
  ROLE_CRUISE_COORDINATOR,
  ROLE_EMPLOYEE_USER,
  ROLE_EXPERIENCES_COORDINATOR,
  ROLE_HOTEL_COORDINATOR,
  ROLE_ICS_STORE_TEAM,
  ROLE_TOUR_COORDINATOR,
} from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';
import useQueryParams from '~/hooks/useQuery';

import canShowTab from '~/utils/canShowTab';

import AddonsPage from './AddonsPageContainer';
import OrdersPage from './AllOrders/OrdersPageContainer';
import BedbankPage from './BedbankPageContainer';
import BundleAndSavePage from './BundleAndSavePageContainer';
import CarHirePage from './CarHire/CarHirePurchasePage';
import CruisesPage from './Cruises/CruisesPurchasePage';
import CustomOffersPage from './CustomOffers';
import FlightsPage from './FlightsPageContainer';
import GiftCardsPage from './GiftCardsPageContainer';
import InsurancesPage from './InsurancesPageContainer';
import { LuxPlusPageContainer } from './LuxPlus';
import MerchandiseOrdersPage from './MerchandiseOrders/MerchandiseOrdersPageContainer';
import PackagesPage from './PackagesPageContainer';
import PointsPurchaseOrdersPageContainer from './PointsPurchaseOrders/PointsPurchaseOrdersPageContainer';
import ToursPage from './ToursPageContainer';

const TAB_ALL_ORDERS = 'allOrders';

// Labels defined at module level so spotlight can pre-register routes without needing user context.
// When adding a new tab, add its label here and reference it in TABS_MAP below.
const TAB_LABELS = {
  [TAB_ALL_ORDERS]: 'All Orders',
  packages: 'Packages',
  giftCards: 'Gift Cards',
  experiences: 'Experiences',
  flights: 'Flights',
  bedbanks: 'Bedbanks',
  tours: 'Tours',
  packagesWithInsurance: 'Insurance',
  carts: 'Abandoned Carts',
  cruises: 'Cruises',
  carHire: 'Car Hire',
  shopOrders: 'Shop',
  pointsPurchase: 'Points Purchase',
  bundle: 'Bundle and Save',
  customOffers: 'Custom Offers',
  luxPlus: 'Lux Plus',
} as const;

preRegisterSpotlightRoutes(
  Object.entries(TAB_LABELS).map(([key, label]) => ({
    path: `/purchases/section/${key}`,
    label: `Purchases / ${label}`,
    section: 'Purchases',
  })),
);

type Tab = {
  label: string;
  pageTitle: string;
  component: ComponentType;
  showTab?: boolean;
};

function defaultTabByRole(user) {
  if (user?.roles?.includes(ROLE_ADMIN_USER) || user?.roles?.includes(ROLE_EMPLOYEE_USER)) {
    return TAB_ALL_ORDERS;
  }

  if (user?.roles?.includes(ROLE_EXPERIENCES_COORDINATOR)) {
    return 'packages';
  }
  if (user?.roles?.includes(ROLE_TOUR_COORDINATOR)) {
    return 'tours';
  }

  if (user?.roles?.includes(ROLE_CRUISE_COORDINATOR)) {
    return 'cruises';
  }

  return TAB_ALL_ORDERS;
}

export default function PurchasesPageContainer() {
  const { user } = useCurrentUser();
  const { section = defaultTabByRole(user) } = useParams<{ section?: Tabs }>();
  const query = useQueryParams();
  const customerId = query.get('customer_id');

  const TABS_MAP = {
    [TAB_ALL_ORDERS]: {
      label: TAB_LABELS[TAB_ALL_ORDERS],
      pageTitle: 'Latest orders - All Types',
      component: OrdersPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    packages: {
      label: TAB_LABELS.packages,
      pageTitle: 'Latest orders',
      component: PackagesPage,
      showTab: canShowTab(user, [
        ROLE_ADMIN_USER,
        ROLE_EMPLOYEE_USER,
        ROLE_EXPERIENCES_COORDINATOR,
        ROLE_ICS_STORE_TEAM,
      ]),
    } as Tab,
    giftCards: {
      label: TAB_LABELS.giftCards,
      pageTitle: 'Latest gift cards',
      component: GiftCardsPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    experiences: {
      label: TAB_LABELS.experiences,
      pageTitle: 'Latest experiences',
      component: AddonsPage,
      showTab: canShowTab(user, [
        ROLE_ADMIN_USER,
        ROLE_EMPLOYEE_USER,
        ROLE_EXPERIENCES_COORDINATOR,
        ROLE_ICS_STORE_TEAM,
      ]),
    } as Tab,
    flights: {
      label: TAB_LABELS.flights,
      pageTitle: 'Latest flights',
      component: FlightsPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    bedbanks: {
      label: TAB_LABELS.bedbanks,
      pageTitle: 'Bedbank orders',
      component: BedbankPage,
      showTab: canShowTab(user, [
        ROLE_ADMIN_USER,
        ROLE_EMPLOYEE_USER,
        ROLE_EXPERIENCES_COORDINATOR,
        ROLE_HOTEL_COORDINATOR,
        ROLE_ICS_STORE_TEAM,
      ]),
    } as Tab,
    tours: {
      label: TAB_LABELS.tours,
      pageTitle: 'Latest tours',
      component: ToursPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_TOUR_COORDINATOR, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    packagesWithInsurance: {
      label: TAB_LABELS.packagesWithInsurance,
      pageTitle: 'Latest insurance',
      component: InsurancesPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    carts: {
      label: TAB_LABELS.carts,
      pageTitle: 'Abandoned Carts',
      component: CartPaneContainer,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
    } as Tab,
    cruises: {
      label: TAB_LABELS.cruises,
      pageTitle: 'Latest cruises',
      component: CruisesPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM, ROLE_CRUISE_COORDINATOR]),
    } as Tab,
    carHire: {
      label: TAB_LABELS.carHire,
      pageTitle: 'Latest bookings',
      component: CarHirePage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    shopOrders: {
      label: TAB_LABELS.shopOrders,
      pageTitle: 'Latest shop orders',
      component: MerchandiseOrdersPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    pointsPurchase: {
      label: TAB_LABELS.pointsPurchase,
      pageTitle: 'Latest points purchases',
      component: PointsPurchaseOrdersPageContainer,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    bundle: {
      label: TAB_LABELS.bundle,
      pageTitle: 'Bundle and Save',
      component: BundleAndSavePage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    customOffers: {
      label: TAB_LABELS.customOffers,
      pageTitle: 'Custom Offers',
      component: CustomOffersPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    luxPlus: {
      label: TAB_LABELS.luxPlus,
      pageTitle: 'Lux Plus',
      component: LuxPlusPageContainer,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
  } as const;

  type Tabs = keyof typeof TABS_MAP;

  const tabList = Object.keys(TABS_MAP);
  const currentTab: Tab = TABS_MAP[section];

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{`Orders / ${currentTab.label}`}</title>
      </Helmet>

      {customerId && <UserBreadcrumbs userId={customerId} currentPage="Orders" />}

      <PageHeader title={currentTab.pageTitle}>
        <AllBusinessesOrderSyncButton />
      </PageHeader>

      <SmsVerifyWarning />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={section} id="orders" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          {tabList
            .filter((tabKey) => {
              const tab: Tab = TABS_MAP[tabKey];

              if (tab.showTab !== undefined) {
                return tab.showTab;
              }

              return true;
            })
            .map((tabKey) => {
              const tab: Tab = TABS_MAP[tabKey];

              return (
                <Tab
                  key={tabKey}
                  label={tab.label}
                  value={tabKey}
                  to={`/purchases/section/${tabKey}${customerId ? `?customer_id=${customerId}` : ''}`}
                  component={Link}
                />
              );
            })}
        </Tabs>
      </Box>

      <Box pt={4}>
        <Box component={currentTab.component} />
      </Box>
    </Container>
  );
}
